import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/context/ToastContext";
import { EMAIL_NOT_VERIFIED_EVENT, EMAIL_NOT_VERIFIED_FALLBACK } from "@/utils/emailNotVerified";

/** A burst of refused requests shows one message, not one per request. */
const QUIET_MS = 3000;

/**
 * When the API refuses a request because the applicant's e-mail is not verified, tells them why and takes them to the verify screen
 * (unless they are already on it). Renders nothing. Mount it once, inside the router and the toast provider.
 */
const EmailNotVerifiedListener = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { showWarning } = useToast();
    const pathnameRef = useRef(pathname);
    const lastShownRef = useRef(0);

    useEffect(() => {
        pathnameRef.current = pathname;
    }, [pathname]);

    useEffect(() => {
        const onRefused = (event: Event) => {
            const now = Date.now();
            if (now - lastShownRef.current < QUIET_MS) return;
            lastShownRef.current = now;

            const message = (event as CustomEvent<string | undefined>).detail || EMAIL_NOT_VERIFIED_FALLBACK;
            showWarning("Verify your email", message);

            const here = pathnameRef.current;
            if (here !== "/verify-email" && here !== "/verify") {
                navigate("/verify-email");
            }
        };

        window.addEventListener(EMAIL_NOT_VERIFIED_EVENT, onRefused);
        return () => window.removeEventListener(EMAIL_NOT_VERIFIED_EVENT, onRefused);
    }, [navigate, showWarning]);

    return null;
};

export default EmailNotVerifiedListener;
